import type { TypographyVariant, TypographyVariantsOptions } from '@mui/material'
import type { CSSProperties } from 'react'
import type { FontThreshold, ParagraphLevel, ParagraphWeight } from './types'

const FONT_FAMILY_INTER = "'Inter', 'Arial', sans-serif"
const FONT_FAMILY_MYNT_SANS_DISPLAY = 'Mynt Sans Display'

const defaultTypography = (
  primaryFont: string = FONT_FAMILY_MYNT_SANS_DISPLAY,
  secondaryFont: string = FONT_FAMILY_INTER,
  fontThreshold: FontThreshold = 'p1',
): TypographyVariantsOptions => {
  const getFontFamily = (variant: TypographyVariant) => (shouldUsePrimaryFont(variant, fontThreshold) ? primaryFont : secondaryFont)

  return {
    fontFamily: primaryFont,
    button: { textTransform: 'unset' },
    h1: {
      fontFamily: getFontFamily('h1'),
      ...TYPOGRAPHY_CONFIG.headings.h1,
    },
    h2: {
      fontFamily: getFontFamily('h2'),
      ...TYPOGRAPHY_CONFIG.headings.h2,
    },
    h3: {
      fontFamily: getFontFamily('h3'),
      ...TYPOGRAPHY_CONFIG.headings.h3,
    },
    p1SemiBold: createParagraphStyle('p1', 'SemiBold', getFontFamily),
    p1Medium: createParagraphStyle('p1', 'Medium', getFontFamily),
    p1Regular: createParagraphStyle('p1', 'Regular', getFontFamily),
    p2SemiBold: createParagraphStyle('p2', 'SemiBold', getFontFamily),
    p2Medium: createParagraphStyle('p2', 'Medium', getFontFamily),
    p2Regular: createParagraphStyle('p2', 'Regular', getFontFamily),
    p3SemiBold: createParagraphStyle('p3', 'SemiBold', getFontFamily),
    p3Medium: createParagraphStyle('p3', 'Medium', getFontFamily),
    p3Regular: createParagraphStyle('p3', 'Regular', getFontFamily),
    p4SemiBold: createParagraphStyle('p4', 'SemiBold', getFontFamily),
    p4Medium: createParagraphStyle('p4', 'Medium', getFontFamily),
    p4Regular: createParagraphStyle('p4', 'Regular', getFontFamily),
  }
}

const getBaseVariant = (variant: TypographyVariant): FontThreshold => variant.substring(0, 2) as ParagraphLevel

const TYPOGRAPHY_HIERARCHY: readonly FontThreshold[] = ['title', 'h1', 'h2', 'h3', 'p1', 'p2', 'p3', 'p4'] as const

const shouldUsePrimaryFont = (variant: TypographyVariant, threshold: FontThreshold): boolean => {
  const baseLevel = getBaseVariant(variant)

  return TYPOGRAPHY_CONFIG.hierarchy.indexOf(baseLevel) < TYPOGRAPHY_CONFIG.hierarchy.indexOf(threshold)
}

/**
 * Default and common typography values
 */
const TYPOGRAPHY_CONFIG = {
  hierarchy: TYPOGRAPHY_HIERARCHY,
  headings: {
    title: { fontSize: '40px', lineHeight: '110%', fontWeight: 500, fontStyle: 'normal' },
    h1: { fontSize: '32px', lineHeight: '110%', fontWeight: 500, fontStyle: 'normal' },
    h2: { fontSize: '24px', lineHeight: '120%', fontWeight: 500, fontStyle: 'normal' },
    h3: { fontSize: '20px', lineHeight: '120%', fontWeight: 500, fontStyle: 'normal' },
  },
  paragraphs: {
    p1: { fontSize: '18px', lineHeight: '140%', letterSpacing: '-0.36px' },
    p2: { fontSize: '16px', lineHeight: '140%', letterSpacing: '-0.32px' },
    p3: { fontSize: '14px', lineHeight: '140%', letterSpacing: '-0.28px' },
    p4: { fontSize: '12px', lineHeight: '140%', letterSpacing: '-0.24px' },
  },
  weights: {
    SemiBold: 600,
    Medium: 500,
    Regular: 400,
  },
} as const

const createParagraphStyle = (
  level: ParagraphLevel,
  weight: ParagraphWeight,
  getFontFamily: (variant: TypographyVariant) => string,
): CSSProperties => ({
  fontFamily: getFontFamily(`${level}${weight}` as TypographyVariant),
  fontStyle: 'normal',
  fontWeight: TYPOGRAPHY_CONFIG.weights[weight],
  ...TYPOGRAPHY_CONFIG.paragraphs[level],
})

export const typography = defaultTypography()
